import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Paper, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Grid2, Typography } from "@mui/material";
import { DataGrid } from '@mui/x-data-grid';
import { IconTrash, IconEdit, IconEye, IconDotsVertical, IconPlus, IconSearch, IconRestore } from "@tabler/icons-react";
import axios from "axios";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";

import BreadCrumbs from "@components/breadCrumbs/breadCrumbs";
import CustomButton from "@components/button/customButton";
import StatusChip from "@components/chip/statusChip";
import ConfirmDialog from "@components/dialog/confirmDialog";
import ControlledTextField from "@components/inputs/controlledTextField";
import ControlledAutocomplete from "@components/inputs/controlledAutocomplete";
import CustomIconButton from "@components/button/customIconButton";

import { useSnackbar } from "@context/snackbarContext";
import { useAuthentication } from "@redux/authentication/hook";

import { AdminApiEndpoints } from "@configuration/apiEndpoints";

import { applyFilters, createSearchFilter, createStatusFilter, createCustomFilter } from "@utils/filter";

import { ACTION } from "@utils/constant";
import { formatDate } from "@utils/formatter";


const defaultQueryFields = {
  search: "",
  postName: "",
  status: "All",
};

export default function FormSetup() {
  const queryClient = useQueryClient()
  const openSnackbar = useSnackbar();
  const { user } = useAuthentication();

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      search: "",
      status: "All",
    }
  })

  const [searchQuery, setSearchQuery] = useState(defaultQueryFields)

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useState(false);

  const [action, setAction] = useState("")
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)

  const [selectedRow, setSelectedRow] = useState(null)

  const { isFetching, error, data } = useQuery({
    queryKey: ['getForm'],
    queryFn: () =>
      axios.get(AdminApiEndpoints.getForm),
    initialData: [],
    select: (res) => {
      const data = res?.data || [];
      return data.sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn));
    },
  })

  // console.log(data)

  const deleteFormMutation = useMutation({
    mutationFn: (id) => {
      return axios.delete(`${AdminApiEndpoints.deleteForm}/${id}`)
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getForm'] })
      openSnackbar("Successfully deleted form", "success");
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      openSnackbar("Failed to delete form", "error");
    }
  })

  const updateFormStatusMutation = useMutation({
    mutationFn: (data) => {
      return axios.put(`${AdminApiEndpoints.updateFormStatus}/${data.id}`, {status: data?.status, by: user?.username})
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getForm'] })
      openSnackbar("Successfully updated form status", "success");
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      openSnackbar("Failed to update form status", "error");
    }
  })

  const handleConfirmSubmit = () => {
    if (action === ACTION.DELETE)
      deleteFormMutation.mutate(selectedRow?.formGuid)
    if (action === ACTION.UPDATE_STATUS)
      updateFormStatusMutation.mutate({id: selectedRow?.formGuid, status: !selectedRow?.status})
  }

  //================================Table================================

  const handleClickStatusChip = (row) => {
    setAction(ACTION.UPDATE_STATUS);
    setSelectedRow(row);
    setOpenConfirmDialog(true);
  }

  const handleClickDelete = () => {
    setAction(ACTION.DELETE);
    setOpenConfirmDialog(true);
    setOpenActionMenu(false);
  }

  const handleClickActions = (event, row) => {
    setMenuAnchorEl(event.currentTarget);
    setOpenActionMenu(true);
    setSelectedRow(row);
  };

  const renderPopper = () => {
    return (
      <Menu
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        anchorEl={menuAnchorEl}
        open={openActionMenu}
        onClose={()=>setOpenActionMenu(false)}
      >
        <MenuItem
          component={RouterLink}
          to={`view?id=${selectedRow?.formGuid}`}
        >
          <ListItemIcon>
            <IconEye size={20}/>
          </ListItemIcon>
          <ListItemText>View</ListItemText>
        </MenuItem>
        <MenuItem
          component={RouterLink}
          to={`edit?id=${selectedRow?.formGuid}`}
        >
          <ListItemIcon>
            <IconEdit size={20}/>
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        {/* <MenuItem
          onClick={handleClickDelete}
        >
          <ListItemIcon>
            <IconTrash size={20}/>
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem> */}
      </Menu>
    );
  };

  //=====================================================================

  //================================Filter================================
      
  const handleClickSearch = (data) => {
    setSearchQuery({...data, questionType: data?.questionType?.questionTypeGuid})
  }

  const handleClickReset = () => {
    reset(defaultQueryFields)
    setSearchQuery(defaultQueryFields)
  }

  const filters = [
    createSearchFilter(searchQuery.search, ["formNumber", "formName", "description"]),
    createStatusFilter(searchQuery.status, "Valid"),
    createCustomFilter(searchQuery.postName, 'posts[0].postName', true),
  ];

  const filteredData = applyFilters(data, filters);

  const postNameOptions = [
    ...new Set(
      data.map((item) => item?.posts?.[0]?.postName).filter(Boolean)
    ),
  ];

  //======================================================================

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
      <BreadCrumbs 
        rightSection={
          <CustomButton component={RouterLink} to={"create"} startIcon={<IconPlus size={13} />}>Create</CustomButton>
        }
      />
      <Paper
        sx={{
          p: "20px 25px",
          boxShadow: "none",
          border: "1px solid #E5E5E5",
          borderRadius: "10px",
        }}
      >
        <Grid2 container spacing={"20px"}>
          <Grid2 size={{ xs: 12 }}>
            <Typography
              fontSize={"0.9rem"}
              color={"#202020"}
              fontWeight={600}
            >
              Search
            </Typography>
          </Grid2>

          <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
            <ControlledTextField 
              name="search"
              control={control}
              label="Search"
            />
          </Grid2>

          <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
            <ControlledAutocomplete 
              name="postName"
              control={control}
              label="Post Name"
              loading={isFetching}
              options={postNameOptions}
            />
          </Grid2>

          <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
            <ControlledAutocomplete 
              name="status"
              control={control}
              label="Status"
              options={["All", "Valid", "Voided"]}
            />
          </Grid2>

          <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }} display={'flex'} justifyContent={"flex-end"} gap={"10px"}>
            <CustomIconButton aria-label="Reset" color="#999999" onClick={handleClickReset}>
              <IconRestore />
            </CustomIconButton>
            <CustomIconButton aria-label="Search" onClick={handleSubmit(handleClickSearch)}>
              <IconSearch />
            </CustomIconButton>
          </Grid2>
        </Grid2>
      </Paper>
      <Paper
        sx={{
          p: "0px 25px",
          boxShadow: "none",
          borderRadius: "10px",
          border: "1px solid #E5E5E5",
        }}
      >
        <DataGrid
          loading={isFetching}
          rows={filteredData}
          localeText={{ noRowsLabel: 'No records found' }}
          getRowId={(row) => row.formGuid}
          columns={[
            {
              field: 'formNumber',
              headerName: 'Form No',
              flex: 1, minWidth: 125,
            },
            {
              field: 'formName',
              headerName: 'Form Name',
              flex: 1, minWidth: 130,
            },
            {
              field: 'description',
              headerName: 'Description',
              flex: 1, minWidth: 125,
            },
            {
              field: 'postName',
              headerName: 'Post Name',
              flex: 1, minWidth: 125,
              valueGetter: (value, row) => row.posts?.[0]?.postName || "-" 
            },
            {
              field: 'postDate',
              headerName: 'Post Date',
              flex: 1, minWidth: 125,
              valueGetter: (value, row) => formatDate(row.posts?.[0]?.postDate) || "-" 
            },
            {
              field: 'status',
              headerName: 'Status',
              flex: 1, minWidth: 100,
              renderCell: (params) => {
                const statusText = params.row.status ? "Valid" : "Voided"
                return <StatusChip status={statusText} />
              },
            },
            {
              field: 'actions',
              headerName: 'Actions',
              sortable: false,
              renderCell: (params) => (
                <IconButton aria-label="Actions" onClick={(event) => handleClickActions(event, params.row)}>
                  <IconDotsVertical />
                </IconButton>
              ),
            },
          ]}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          pageSizeOptions={[10,25,50]}
          disableRowSelectionOnClick
          disableColumnResize
          disableColumnMenu
          disableColumnSelector
          disableDensitySelector
          sx={{ border: 0 }}
        />
        {renderPopper()}
      </Paper>
      
      <ConfirmDialog 
        open={openConfirmDialog}
        onClose={()=>setOpenConfirmDialog(false)}
        onSubmit={handleConfirmSubmit}
        loading={deleteFormMutation.isPending || updateFormStatusMutation.isPending}
        type={action === ACTION.DELETE ? "error" : "info"}
        dialogTitle={`${action} Confirmation`}
        dialogDescription={
          action === ACTION.DELETE ? 
            'Are you sure you want to delete the following form?'
          : 
            `Update the following form status to ${selectedRow?.statusText === "Active" ? 'Inactive' : 'Active'}?`
        }
        items={[selectedRow?.formName]}
      />
    </div>
  );
}